import { api } from "../api";
import { API_ROUTES } from "../../routes/apiRoutes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import qs from "qs";
import { useTranslation } from "react-i18next";
import { useToast } from "../../components/shared/ToastProvider";
import { capitalizeString } from "../helpers/formatStrings";

const useUsers = (page = 1, perPage = 10, searchParams) => {
  return useQuery({
    queryKey: [
      "users",
      searchParams?.sortBy,
      searchParams?.sortDesc,
      searchParams?.searchQuery,
      searchParams?.isDependent,
      ...(searchParams?.roles ?? []),
      ...(searchParams?.statuses ?? []),
      page,
      perPage,
    ],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.ALL_USERS, {
        params: {
          page,
          per_page: perPage,
          search_query: searchParams?.searchQuery,
          sort_by: searchParams?.sortBy,
          sort_desc: searchParams?.sortDesc,
          roles: searchParams?.roles,
          statuses: searchParams?.statuses,
          is_dependent: searchParams?.isDependent,
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
      });
      return res.data;
    },
  });
};

const useIndependentUsers = (searchParams) => {
  return useQuery({
    queryKey: ["users", "independent", searchParams?.searchQuery],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.INDEPENDENT_USERS, {
        params: {
          search_query: searchParams?.searchQuery,
        },
      });
      return res.data;
    },
  });
};

const useUsersPreview = (searchParams) => {
  return useQuery({
    queryKey: ["users", "users-preview", searchParams],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.USERS_PREVIEW, {
        params: {
          search_query: searchParams?.searchQuery,
          roles: searchParams?.roles,
          statuses: searchParams?.statuses,
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
      });
      return res?.data?.data;
    },
  });
};

const useUserProfile = (userId) => {
  return useQuery({
    queryKey: ["users", "profile", userId],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.USER_PROFILE, { params: { user_id: userId } });
      return res?.data?.data;
    },
  });
};

const useUserWrite = (mode = "write") => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (data) => {
      switch (mode) {
        case "edit":
          return api.patch(API_ROUTES.ALL_USERS, data);
        default:
          return api.post(API_ROUTES.ALL_USERS, data);
      }
    },
    onError: (error) => {
      const status = error?.response?.status;
      let defaultMessage =
        mode === "edit" ? "userManagement.editUser.error" : "userManagement.addUser.error";
      if (status === 400) {
        const errors = error?.response?.data?.errors;
        if (errors) {
          const message = Object.values(errors)[0]?.[0];
          addToast(capitalizeString(message) ?? t(defaultMessage), "error");
          return;
        }
        addToast(capitalizeString(error?.response?.data?.message) ?? t(defaultMessage), "error");
      } else if (status !== 403 && status !== 409) {
        addToast(capitalizeString(error?.response?.data?.message) ?? t(defaultMessage), "error");
      }
    },
    onSuccess: () => {
      let message =
        mode === "edit" ? "userManagement.editUser.success" : "userManagement.addUser.success";
      addToast(t(message), "success");
      queryClient.refetchQueries({ queryKey: ["users"] });
    },
  });
};

const useUserDeactivate = (userId) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (deactivate) => {
      return api.post(API_ROUTES.DEACTIVATE_USER, {
        user_id: userId,
        deactivate,
      });
    },
    onError: (error) => {
      const status = error?.response?.status;
      if (status === 400) {
        const errors = error?.response?.data?.errors;
        if (errors) {
          const message = Object.values(errors)[0]?.[0];
          addToast(message ?? t("userManagement.toggleUserStatus.error"), "error");
          return;
        }
        addToast(
          error?.response?.data?.message ?? t("userManagement.toggleUserStatus.error"),
          "error",
        );
      } else if (status !== 403 && status !== 409) {
        addToast(
          error?.response?.data?.message ?? t("userManagement.toggleUserStatus.error"),
          "error",
        );
      }
    },
    onSuccess: () => {
      addToast(t("userManagement.toggleUserStatus.success"), "success");
      queryClient.refetchQueries({ queryKey: ["users"] });
    },
  });
};

const useResendInvites = (userIds, mode = "single") => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: () => {
      return api.post(API_ROUTES.RESEND_INVITE, { user_ids: userIds });
    },
    onError: (error) => {
      const defaultMessage =
        mode === "single"
          ? "userManagement.resendInvite.error"
          : "userManagement.resendInvite.errorBulk";
      const status = error?.response?.status;
      if (status === 400) {
        const errors = error?.response?.data?.errors;
        if (errors) {
          const message = Object.values(errors)[0]?.[0];
          addToast(message ?? t(defaultMessage), "error");
          return;
        }
        addToast(error?.response?.data?.message ?? t(defaultMessage), "error");
      } else if (status !== 403 && status !== 409) {
        addToast(error?.response?.data?.message ?? t(defaultMessage), "error");
      }
    },
    onSuccess: () => {
      addToast(
        t(
          mode === "single"
            ? "userManagement.resendInvite.success"
            : "userManagement.resendInvite.successBulk",
        ),
        "success",
      );
    },
  });
};

const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id) => api.delete(API_ROUTES.DELETE_USER(id)),
    onError: (error) => {
      const status = error?.response?.status;
      if (status !== 403 && status !== 409) {
        addToast(error?.response?.data?.message ?? t("userManagement.deleteUser.error"), "error");
      }
    },
    onSuccess: () => {
      addToast(t("userManagement.deleteUser.success"), "success");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export {
  useUsers,
  useUsersPreview,
  useIndependentUsers,
  useUserProfile,
  useUserWrite,
  useUserDeactivate,
  useResendInvites,
  useDeleteUser,
};
